// Pure loaders for user-level settings persisted in localStorage.
// Each function defends against stale / invalid values by falling back
// to a safe default, so callers never have to validate themselves.
//
// These stay pure (take localStorage via the Storage interface) so they
// can be unit-tested without DOM. The caller (legacy app bootstrap)
// seeds the god-object `state` with the results.
//
// Per-deck prefs (dirRatio, difficulty, levelFloor, tagFilter): the
// storage shape is `{[listId]: value}` JSON so switching decks restores
// each deck's own preferences without the user retuning sliders every
// time. Legacy single-value storage (pre-multi-deck) migrates on first
// load by slotting the old value under DEFAULT_LIST_ID — no user-
// visible hiccup, the next save writes the new shape and the legacy
// branch never runs again for that user.

import { DEFAULT_LIST_ID } from "../config/lists";
import { STORAGE_KEYS } from "../config/storage-keys";
import { LANGS, defaultTargetFor, type Lang, type Locale } from "../config/langs";
import { DIFFICULTY_MODES, type DifficultyKey } from "../config/modes";
import { LEVELS, type Level } from "../config/defaults";
import {
  AXIS_TAGS,
  TAG_AXES,
  axisForTag,
  emptyTagFilter,
  isTagFilterEmpty,
  type TagAxis,
  type TagFilter,
} from "../config/tags";

/** Read a per-deck JSON bag `{[listId]: T}` from storage, migrating
 *  legacy single-value formats (number / string / array) into that
 *  shape by slotting them under DEFAULT_LIST_ID. Returns the raw
 *  per-deck record or null when the key is absent. The loader is
 *  responsible for extracting + validating listId's slot (the default
 *  shape differs per pref: number, string, object). */
function readPerDeckBag(
  key: string,
  isLegacy: (v: unknown) => boolean,
  ls: Storage,
): Record<string, unknown> | null {
  const raw = ls.getItem(key);
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Legacy bare-string formats (e.g. "easy" for difficulty, "B1" for
    // levelFloor) aren't valid JSON — fall back to treating the raw
    // string as a potential legacy value.
    parsed = raw;
  }
  if (isLegacy(parsed)) {
    return { [DEFAULT_LIST_ID]: parsed };
  }
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return parsed as Record<string, unknown>;
  }
  return null;
}

/** Write a single listId's entry into the per-deck bag at `key`. */
function writePerDeckBag(
  key: string,
  listId: string,
  value: unknown,
  isLegacy: (v: unknown) => boolean,
  ls: Storage,
): void {
  const bag = readPerDeckBag(key, isLegacy, ls) ?? {};
  if (value === null || value === undefined) delete bag[listId];
  else bag[listId] = value;
  if (Object.keys(bag).length === 0) {
    ls.removeItem(key);
    return;
  }
  ls.setItem(key, JSON.stringify(bag));
}

/** 0..100. Probability (percent) of prompting in the TARGET language
 *  (so the user writes the SOURCE language). Default: 50 (balanced).
 *  Per-deck: each deck remembers its own ratio. */
export function loadDirRatio(
  listId: string,
  ls: Storage = localStorage,
): number {
  const bag = readPerDeckBag(
    STORAGE_KEYS.DIR_RATIO,
    (v): boolean => typeof v === "number",
    ls,
  );
  const v = bag?.[listId];
  if (typeof v === "number" && Number.isFinite(v)) {
    return Math.max(0, Math.min(100, v));
  }
  return 50;
}

export function saveDirRatio(
  listId: string,
  value: number,
  ls: Storage = localStorage,
): void {
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  writePerDeckBag(
    STORAGE_KEYS.DIR_RATIO,
    listId,
    clamped,
    (v): boolean => typeof v === "number",
    ls,
  );
}

/** Current target lang. Falls back to locale's default target. */
export function loadTargetLang(
  locale: Locale,
  ls: Storage = localStorage,
): Lang {
  const v = ls.getItem(STORAGE_KEYS.TARGET);
  if (v && (LANGS as readonly string[]).includes(v)) return v as Lang;
  return defaultTargetFor(locale);
}

/** Current source lang. Returns null when unset — caller falls back to
 *  the UI locale's native language. Kept explicit so "no override" vs
 *  "same as locale" stays distinguishable. */
export function loadSourceLang(ls: Storage = localStorage): Lang | null {
  const v = ls.getItem(STORAGE_KEYS.SOURCE);
  if (v && (LANGS as readonly string[]).includes(v)) return v as Lang;
  return null;
}

/** Active difficulty mode — defaults to "adaptive". Per-deck: each
 *  deck remembers its own difficulty (general/capitales can diverge). */
export function loadDifficulty(
  listId: string,
  ls: Storage = localStorage,
): DifficultyKey {
  const bag = readPerDeckBag(
    STORAGE_KEYS.DIFFICULTY,
    (v): boolean => typeof v === "string" && DIFFICULTY_MODES[v as DifficultyKey] != null,
    ls,
  );
  const v = bag?.[listId];
  if (typeof v === "string" && DIFFICULTY_MODES[v as DifficultyKey]) {
    return v as DifficultyKey;
  }
  return "adaptive";
}

export function saveDifficulty(
  listId: string,
  value: DifficultyKey,
  ls: Storage = localStorage,
): void {
  writePerDeckBag(
    STORAGE_KEYS.DIFFICULTY,
    listId,
    value,
    (v): boolean => typeof v === "string" && DIFFICULTY_MODES[v as DifficultyKey] != null,
    ls,
  );
}

/** Self-declared starting level (acts as a floor for the estimator).
 *  Null = no floor, estimator is free to start at A1. Per-deck: only
 *  meaningful for bilingual decks (CEFR doesn't apply to mono), but
 *  stored uniformly so the loader doesn't branch on shape. */
export function loadLevelFloor(
  listId: string,
  ls: Storage = localStorage,
): Level | null {
  const bag = readPerDeckBag(
    STORAGE_KEYS.LEVEL_FLOOR,
    (v): boolean => typeof v === "string" && (LEVELS as readonly string[]).includes(v),
    ls,
  );
  const v = bag?.[listId];
  if (typeof v === "string" && (LEVELS as readonly string[]).includes(v)) {
    return v as Level;
  }
  return null;
}

export function saveLevelFloor(
  listId: string,
  value: Level | null,
  ls: Storage = localStorage,
): void {
  writePerDeckBag(
    STORAGE_KEYS.LEVEL_FLOOR,
    listId,
    value,
    (v): boolean => typeof v === "string" && (LEVELS as readonly string[]).includes(v),
    ls,
  );
}

/** Coerce an arbitrary payload to a valid TagFilter. Accepts:
 *   - an array of strings (very-old flat format): each tag rehomes to
 *     its declared axis.
 *   - an object `{class: [], domain: [], register: []}`: each axis
 *     filters to its allowed vocabulary.
 *  Anything else returns an empty filter. Kept pure so it plugs into
 *  both the legacy-upgrade branch and the per-deck reader below. */
function coerceTagFilter(parsed: unknown): TagFilter {
  const out = emptyTagFilter();
  if (Array.isArray(parsed)) {
    for (const v of parsed) {
      if (typeof v !== "string" || !v) continue;
      const axis = axisForTag(v);
      if (axis) out[axis].push(v);
    }
    return out;
  }
  if (parsed && typeof parsed === "object") {
    for (const axis of TAG_AXES) {
      const slot = (parsed as Record<string, unknown>)[axis];
      if (!Array.isArray(slot)) continue;
      const allowed = AXIS_TAGS[axis] as readonly string[];
      for (const v of slot) {
        if (typeof v === "string" && v && allowed.includes(v)) {
          out[axis].push(v);
        }
      }
    }
  }
  return out;
}

/** Detect a legacy (pre-per-deck) tag filter payload. Matches either a
 *  flat string[] (very old) or an object with a TAG_AXIS key whose
 *  value is an array (post-multi-axis, pre-per-deck). The new per-deck
 *  shape maps listId → TagFilter so axis keys never sit at the top. */
function isLegacyTagFilter(v: unknown): boolean {
  if (Array.isArray(v)) return true;
  if (!v || typeof v !== "object") return false;
  for (const axis of TAG_AXES) {
    if (Array.isArray((v as Record<string, unknown>)[axis])) return true;
  }
  return false;
}

/** Active tag filter (see `config/tags.ts`). Stored per-deck as
 *  `{[listId]: {class: [], domain: [], register: []}}`. Legacy formats
 *  migrate to DEFAULT_LIST_ID's slot on first load.
 *
 *  Every return path produces a well-formed TagFilter so callers never
 *  need to null-check axis arrays. */
export function loadTagFilter(
  listId: string,
  ls: Storage = localStorage,
): TagFilter {
  const raw = ls.getItem(STORAGE_KEYS.TAG_FILTER);
  if (!raw) return emptyTagFilter();
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyTagFilter();
  }
  if (isLegacyTagFilter(parsed)) {
    // Legacy — the stored value IS the TagFilter. Only return it for
    // DEFAULT_LIST_ID; other decks get empty. The next save upgrades
    // the key to the per-deck shape.
    return listId === DEFAULT_LIST_ID ? coerceTagFilter(parsed) : emptyTagFilter();
  }
  if (parsed && typeof parsed === "object") {
    const slot = (parsed as Record<string, unknown>)[listId];
    return coerceTagFilter(slot);
  }
  return emptyTagFilter();
}

/** Persist the tag filter for a deck. Fully-empty filter = remove the
 *  deck's slot (and the whole key when no decks have filters) so the
 *  default loader path kicks in cleanly. */
export function saveTagFilter(
  listId: string,
  filter: TagFilter,
  ls: Storage = localStorage,
): void {
  // Read current bag (migrating legacy on the fly).
  let bag: Record<string, TagFilter> = {};
  const raw = ls.getItem(STORAGE_KEYS.TAG_FILTER);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (isLegacyTagFilter(parsed)) {
        bag[DEFAULT_LIST_ID] = coerceTagFilter(parsed);
      } else if (parsed && typeof parsed === "object") {
        for (const key of Object.keys(parsed as Record<string, unknown>)) {
          const slot = coerceTagFilter(
            (parsed as Record<string, unknown>)[key],
          );
          if (!isTagFilterEmpty(slot)) bag[key] = slot;
        }
      }
    } catch {
      bag = {};
    }
  }
  if (isTagFilterEmpty(filter)) {
    delete bag[listId];
  } else {
    // Normalize: drop axes that are empty so the persisted payload
    // stays compact and future readers only see meaningful slots.
    const normalized: Partial<Record<TagAxis, string[]>> = {};
    for (const axis of TAG_AXES) {
      if (filter[axis].length) normalized[axis] = [...filter[axis]];
    }
    bag[listId] = normalized as TagFilter;
  }
  if (Object.keys(bag).length === 0) {
    ls.removeItem(STORAGE_KEYS.TAG_FILTER);
    return;
  }
  ls.setItem(STORAGE_KEYS.TAG_FILTER, JSON.stringify(bag));
}

/** Per-deck TTS configuration. `enabled` gates both the speaker ♪
 *  button (hidden when false) and any TTS firing; `autoplay` is a
 *  sub-option only consulted when `enabled`. Defaults depend on deck
 *  shape: bilingual decks start with audio on (target-language
 *  pronunciation is genuinely useful), mono decks start with audio
 *  off (both faces are in the user's own language — hearing "France"
 *  spoken in French adds nothing). */
export type TtsConfig = { enabled: boolean; autoplay: boolean };

function defaultTtsConfig(shape: "bilingual" | "mono"): TtsConfig {
  return shape === "bilingual"
    ? { enabled: true, autoplay: false }
    : { enabled: false, autoplay: false };
}

function isTtsConfigShape(v: unknown): v is TtsConfig {
  return (
    typeof v === "object" &&
    v !== null &&
    typeof (v as TtsConfig).enabled === "boolean" &&
    typeof (v as TtsConfig).autoplay === "boolean"
  );
}

export function loadTtsConfig(
  listId: string,
  shape: "bilingual" | "mono",
  ls: Storage = localStorage,
): TtsConfig {
  // One-shot migration from the pre-1c global TTS_AUTOPLAY key. If the
  // legacy flag was set to "1", the user had audio autoplay on, so
  // carry that intent forward into the bag under the current deck (we
  // can't know which decks they cared about, but starting ON for the
  // current one matches expectations). Legacy key is NOT deleted here
  // — boot cleanup handles that once to avoid races across concurrent
  // tabs reading during migration.
  const bag = readPerDeckBag(
    STORAGE_KEYS.TTS_CONFIG,
    (v): boolean => isTtsConfigShape(v),
    ls,
  );
  const v = bag?.[listId];
  if (isTtsConfigShape(v)) return v;
  const legacy = ls.getItem(STORAGE_KEYS.TTS_AUTOPLAY);
  if (legacy === "1") {
    // Legacy user had autoplay on — respect that intent by starting
    // this deck with both flags true regardless of shape (the legacy
    // UI had no "audio off" state, it was just an autoplay switch).
    return { enabled: true, autoplay: true };
  }
  return defaultTtsConfig(shape);
}

export function saveTtsConfig(
  listId: string,
  cfg: TtsConfig,
  ls: Storage = localStorage,
): void {
  writePerDeckBag(
    STORAGE_KEYS.TTS_CONFIG,
    listId,
    cfg,
    (v): boolean => isTtsConfigShape(v),
    ls,
  );
}

/** Remove the legacy global TTS autoplay key. Called once at boot
 *  after each deck's TTS config has had a chance to load from it. */
export function clearLegacyTtsAutoplay(ls: Storage = localStorage): void {
  ls.removeItem(STORAGE_KEYS.TTS_AUTOPLAY);
}

/** Pure predicate: does a card pass the given tag filter?
 *  Semantics: AND across axes (every constrained axis must match),
 *  OR within an axis (any one tag matches). An axis with an empty
 *  array imposes no constraint — not even a requirement that the card
 *  carry a tag in that axis. Fully-empty filter trivially matches. */
export function cardMatchesTagFilter(
  cardTags: readonly string[],
  filter: TagFilter,
): boolean {
  const tagSet = cardTags.length ? new Set(cardTags) : null;
  for (const axis of TAG_AXES) {
    const wanted = filter[axis];
    if (!wanted.length) continue;
    if (!tagSet) return false;
    let hit = false;
    for (const w of wanted) {
      if (tagSet.has(w)) { hit = true; break; }
    }
    if (!hit) return false;
  }
  return true;
}
